import React, { useEffect, useRef, useState } from "react";
import { IoCloseSharp } from "react-icons/io5";
import "./MultiSelect.css";

const MultiSelectDropdown = ({ options = [], onChangefun, value }) => {
  const [selectedOptions, setSelectedOptions] = useState(() => {
    if (value !== undefined) {
      return Array.isArray(value) ? value : value ? [value] : [];
    }
    const stored = sessionStorage.getItem("masterselecteditems");
    return stored ? JSON.parse(stored) : [];
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (value !== undefined) {
      setSelectedOptions(Array.isArray(value) ? value : value ? [value] : []);
    }
  }, [value]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  useEffect(() => {
    if (!isDropdownOpen) return;
    const isInside = (e) => {
      if (!containerRef.current) return false;
      if (containerRef.current.contains(e.target)) return true;
      const rect = containerRef.current.getBoundingClientRect();
      return (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      );
    };

    const onPointerDown = (e) => {
      if (isInside(e)) return;
      // Do not close on pointerdown if the user is clicking an interactive element
      // (such as a Submit or Close button). Closing on pointerdown causes in-flow
      // dropdowns to collapse immediately, which shifts the button position and
      // causes the browser to abort the click event on the button.
      if (
        e.target &&
        e.target.closest &&
        e.target.closest("button, [role='button'], input, select, textarea, a")
      ) {
        return;
      }
      setIsDropdownOpen(false);
    };

    const onClickOutside = (e) => {
      if (isInside(e)) return;
      setIsDropdownOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("click", onClickOutside);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("click", onClickOutside);
    };
  }, [isDropdownOpen]);

  const getOptionId = (opt) => {
    if (typeof opt !== "object" || opt === null) return opt;
    return (
      opt.id !== undefined
        ? opt.id
        : opt.subDivisionId !== undefined
          ? opt.subDivisionId
          : opt.divisionId !== undefined
            ? opt.divisionId
            : opt.circleId !== undefined
              ? opt.circleId
              : opt.wingId !== undefined
                ? opt.wingId
                : opt.areaId !== undefined
                  ? opt.areaId
                  : opt.ID !== undefined
                    ? opt.ID
                    : opt.name
    );
  };

  const getOptionName = (opt) => {
    if (typeof opt !== "object" || opt === null) return String(opt ?? "");
    return (
      opt.name ||
      opt.area ||
      opt.subDivision ||
      opt.division ||
      opt.circle ||
      opt.wing ||
      String(getOptionId(opt) ?? "")
    );
  };

  const handleOptionClick = (id, name) => {
    let newSelectedOptions = [];
    const isPresent = selectedOptions.some((item) => {
      const itemId =
        typeof item === "object" && item !== null ? getOptionId(item) : item;
      return String(itemId) === String(id);
    });
    if (isPresent) {
      newSelectedOptions = selectedOptions.filter((selectedOption) => {
        const selId =
          typeof selectedOption === "object" && selectedOption !== null
            ? getOptionId(selectedOption)
            : selectedOption;
        return String(selId) !== String(id);
      });
    } else {
      newSelectedOptions = [...selectedOptions, id];
    }
    sessionStorage.setItem(
      "masterselecteditems",
      JSON.stringify(newSelectedOptions),
    );
    setSelectedOptions(newSelectedOptions);

    if (typeof onChangefun === "function") {
      onChangefun(newSelectedOptions);
    }
  };

  const isSelected = (optionId) => {
    const targetStr = String(optionId);
    return selectedOptions.some((item) => {
      const itemId =
        typeof item === "object" && item !== null ? getOptionId(item) : item;
      return String(itemId) === targetStr;
    });
  };

  const isAllSelected =
    Array.isArray(options) &&
    options.length > 0 &&
    options.every((option) => isSelected(getOptionId(option)));

  const handleSelectAll = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    let newSelectedOptions = [];
    if (!isAllSelected) {
      newSelectedOptions = options.map((opt) => getOptionId(opt));
    } else {
      newSelectedOptions = [];
    }
    sessionStorage.setItem(
      "masterselecteditems",
      JSON.stringify(newSelectedOptions),
    );
    setSelectedOptions(newSelectedOptions);

    if (typeof onChangefun === "function") {
      onChangefun(newSelectedOptions);
    }
  };

  return (
    <div className="multi-select-container" ref={containerRef}>
      <button
        className="dropdown-header rounded-lg multi-sel project-box"
        style={{
          overflowX: "hidden",
          minHeight: "38px",
          height: "38px",
          backgroundColor: "#f1f5fb",
          border: "2px solid #f1f5fb",
          borderRadius: "8px",
          width: "100%",
          textAlign: "left",
          cursor: "pointer",
          padding: "2px 10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        onClick={toggleDropdown}
        type="button"
      >
        <span
          className="d-flex selected-items"
          onWheel={(e) => {
            if (e.deltaY !== 0) {
              e.currentTarget.scrollLeft += e.deltaY;
            }
          }}
        >
          {selectedOptions.length > 0
            ? options
                .filter((opt) => isSelected(getOptionId(opt)))
                .map((opt) => {
                  const optId = getOptionId(opt);
                  const optName = getOptionName(opt);
                  return (
                    <span
                      key={optId}
                      style={{
                        backgroundColor: "#C7EBFF",
                        width: "auto",
                        whiteSpace: "nowrap",
                        borderRadius: "4px",
                        padding: "2px 6px 2px 8px",
                        fontSize: "13px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                      className="mr-2 p-1 selected-tag-item"
                    >
                      <span>{optName}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label={`Remove ${optName}`}
                        className="remove-pill-btn"
                        style={{
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginLeft: "2px",
                          lineHeight: 1,
                          color: "#004ac3",
                          fontWeight: "bold",
                          borderRadius: "50%",
                          padding: "1px",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOptionClick(optId, optName);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.stopPropagation();
                            e.preventDefault();
                            handleOptionClick(optId, optName);
                          }
                        }}
                      >
                        <IoCloseSharp size={13} />
                      </span>
                    </span>
                  );
                })
            : "Select options"}
        </span>
        <span className="dropdown-arrow">{isDropdownOpen ? "▲" : "▼"}</span>
      </button>
      {isDropdownOpen && (
        <ul className="dropdown-list fsi-14">
          {Array.isArray(options) && options.length > 0 ? (
            <>
              <li className="select-all-item" key="select-all">
                <button
                  type="button"
                  className="select-all-btn"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    width: "100%",
                    textAlign: "left",
                    cursor: "pointer",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                  }}
                  onClick={handleSelectAll}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(isAllSelected)}
                    onChange={handleSelectAll}
                    onClick={(e) => e.stopPropagation()}
                    style={{ marginRight: "10px", cursor: "pointer" }}
                  />
                  <span>Select All</span>
                </button>
              </li>
              {options.map((option) => {
                const optId = getOptionId(option);
                const optName = getOptionName(option);
                const checked = isSelected(optId);
                return (
                  <li className="" key={optId || optName}>
                    <button
                      type="button"
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        width: "100%",
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                      onClick={() => handleOptionClick(optId, optName)}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(checked)}
                        onChange={() => handleOptionClick(optId, optName)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ marginRight: "10px", cursor: "pointer" }}
                      />
                      {optName}
                    </button>
                  </li>
                );
              })}
            </>
          ) : (
            <li
              className="px-3 py-2 text-center text-muted fsi-12"
              style={{ cursor: "default", listStyle: "none" }}
            >
              No Data Available
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
